import React, { useState, useEffect } from 'react';
import { getAllTickets, getClientsForFilter, getProductsForFilter } from '../../services/supportApi';
import './StaffDashboard.css';

function StaffDashboard({ onSelectTicket, onViewAsClient, refreshTrigger }) {
  const [tickets, setTickets] = useState([]);
  const [clients, setClients] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    client: '',
    status: '',
    product: ''
  });
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 25;

  useEffect(() => {
    fetchClients();
    fetchProducts();
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [filters, page, refreshTrigger]);

  const fetchClients = async () => {
    try {
      const response = await getClientsForFilter();
      setClients(response.data);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await getProductsForFilter();
      setProducts(response.data);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchTickets = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getAllTickets(filters, page);
      // Staff response is paginated {count, results}; client response is a plain array
      if (response.data && response.data.results !== undefined) {
        setTickets(response.data.results);
        setTotalCount(response.data.count);
      } else {
        setTickets(response.data);
        setTotalCount(response.data.length);
      }
    } catch (err) {
      console.error('Failed to fetch tickets:', err);
      setError('Failed to load tickets. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open':
        return 'status-open';
      case 'in_progress':
        return 'status-in-progress';
      case 'completed':
        return 'status-completed';
      case 'closed':
        return 'status-closed';
      default:
        return '';
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setPage(1);
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setPage(1);
    setFilters({ client: '', status: '', product: '' });
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const statusOptions = [
    { value: '', label: 'Active' },
    { value: 'open', label: 'Open' },
    { value: 'in_progress', label: 'In Progress' },
    { value: 'completed', label: 'Completed' },
    { value: 'closed', label: 'Closed' }
  ];

  return (
    <div className="staffDashboard">
      <div className="staffDashboard-header">
        <h2>Support Tickets</h2>
        <button className="staffDashboard-viewAsClient" onClick={onViewAsClient}>
          View as Client
        </button>
      </div>

      <div className="staffDashboard-filters">
        <select
          name="client"
          value={filters.client}
          onChange={handleFilterChange}
        >
          <option value="">All Clients</option>
          {clients.map(client => (
            <option key={client.id} value={client.id}>{client.name}</option>
          ))}
        </select>

        <select
          name="status"
          value={filters.status}
          onChange={handleFilterChange}
        >
          {statusOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>

        <select
          name="product"
          value={filters.product}
          onChange={handleFilterChange}
        >
          <option value="">All Products</option>
          {products.map(product => (
            <option key={product.id} value={product.id}>{product.name}</option>
          ))}
        </select>

        {(filters.client || filters.status || filters.product) && (
          <button className="staffDashboard-clearFilters" onClick={clearFilters}>
            Clear Filters
          </button>
        )}
      </div>

      {loading ? (
        <div className="staffDashboard-loading">Loading tickets...</div>
      ) : error ? (
        <div className="staffDashboard-error">
          {error}
          <button onClick={fetchTickets} className="staffDashboard-retry">Try Again</button>
        </div>
      ) : tickets.length === 0 ? (
        <div className="staffDashboard-empty">No tickets found.</div>
      ) : (
        <div className="staffDashboard-table-wrapper">
          <table className="staffDashboard-table">
            <thead>
              <tr>
                <th className="staffDashboard-unread-col"></th>
                <th>ID</th>
                <th>Subject</th>
                <th>Type</th>
                <th>Product</th>
                <th>Client</th>
                <th>Submitted By</th>
                <th>Status</th>
                <th>Created</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {tickets.map(ticket => (
                <tr key={ticket.id} onClick={() => onSelectTicket(ticket.id)}>
                  <td className="staffDashboard-unread-col">
                    {ticket.has_unread && <span className="staffDashboard-unread-dot" />}
                  </td>
                  <td className="staffDashboard-id">{ticket.client_code ? `${ticket.client_code}-${ticket.id}` : `#${ticket.id}`}</td>
                  <td className="staffDashboard-subject">{ticket.subject}</td>
                  <td>{ticket.form_type_name}</td>
                  <td>{ticket.product_name || '—'}</td>
                  <td>{ticket.client_name || '—'}</td>
                  <td>{ticket.submitted_by}</td>
                  <td>
                    <span className={`staffDashboard-status ${getStatusClass(ticket.status)}`}>
                      {ticket.status_display}
                    </span>
                  </td>
                  <td>{formatDate(ticket.created_at)}</td>
                  <td>{formatDate(ticket.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="staffDashboard-footer">
        <span className="staffDashboard-count">
          {totalCount} ticket{totalCount !== 1 ? 's' : ''}
        </span>
        {totalPages > 1 && (
          <div className="staffDashboard-pagination">
            <button
              className="staffDashboard-pageBtn"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1}
            >
              Prev
            </button>
            <span className="staffDashboard-pageInfo">
              {page} / {totalPages}
            </span>
            <button
              className="staffDashboard-pageBtn"
              onClick={() => setPage(p => p + 1)}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default StaffDashboard;
